import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useFMGyms } from '@/hooks/useFMGyms';
import { useOfflineSync } from '@/hooks/useOfflineSync';

const FMContext = createContext();

const FM_SELECTED_GYM_KEY = 'fm_selected_gym';

/**
 * FMProvider - provides gym selection state and offline sync across all FM pages.
 * Persists selectedGymId to localStorage.
 * Exposes offline sync state (isOnline, pendingSyncCount, queue) to all FM components.
 */
export function FMProvider({ children }) {
  const { gyms, loading, error, refetch } = useFMGyms();
  const {
    isOnline,
    pendingSyncCount,
    syncing,
    lastSyncTime,
    syncNow,
    queue,
  } = useOfflineSync();
  const [selectedGymId, setSelectedGymId] = useState(() => {
    return localStorage.getItem(FM_SELECTED_GYM_KEY) || null;
  });

  // On gyms load: validate saved selection or auto-select first gym
  useEffect(() => {
    if (loading || gyms.length === 0) return;

    const savedId = localStorage.getItem(FM_SELECTED_GYM_KEY);
    const savedGymExists = gyms.some(g => (g.gym_id || g._id) === savedId);

    if (savedGymExists) {
      setSelectedGymId(savedId);
    } else {
      // Auto-select first gym
      const firstId = gyms[0]?.gym_id || gyms[0]?._id;
      setSelectedGymId(firstId);
      if (firstId) localStorage.setItem(FM_SELECTED_GYM_KEY, firstId);
    }
  }, [gyms, loading]);

  // Persist selection changes
  const handleSetSelectedGymId = (gymId) => {
    setSelectedGymId(gymId);
    if (gymId) {
      localStorage.setItem(FM_SELECTED_GYM_KEY, gymId);
    } else {
      localStorage.removeItem(FM_SELECTED_GYM_KEY);
    }
  };

  // Full selected gym object
  const selectedGym = useMemo(() => {
    if (!selectedGymId || gyms.length === 0) return null;
    return gyms.find(g => (g.gym_id || g._id) === selectedGymId) || null;
  }, [selectedGymId, gyms]);

  const value = {
    selectedGymId,
    setSelectedGymId: handleSetSelectedGymId,
    selectedGym,
    gyms,
    loading,
    error,
    refetch,
    // Offline sync state
    isOnline,
    pendingSyncCount,
    syncing,
    lastSyncTime,
    syncNow,
    queue,
  };

  return (
    <FMContext.Provider value={value}>
      {children}
    </FMContext.Provider>
  );
}

/**
 * useFM - access FM context values from any FM page.
 */
export function useFM() {
  const context = useContext(FMContext);
  if (!context) {
    throw new Error('useFM must be used within FMProvider');
  }
  return context;
}
